import type { PreferenceValues } from '../contract/types';
import { numberFormat } from './intl-cache';

/**
 * How many fractional digits a percent point shows when the caller does not say.
 *
 * A ceiling, not a width: `18` stays `18%` and `18.1` stays `18.1%`.
 */
const DEFAULT_MAXIMUM_DIGITS = 2;

export type PercentFormatOptions = {
  /**
   * Most fractional digits to show.
   *
   * A ceiling, not a width. Defaults to 2, so `18` stays `18%` and `18.632`
   * becomes `18.63%`. `0` rounds to a whole percent.
   */
  readonly maximumDigits?: number;
};

/**
 * Formats a percent point for display.
 *
 * The value is the percentage itself, so `18.632` is eighteen point six three
 * two percent. `Intl` formats that number with the percent unit. Dividing by
 * 100 first would round some halfway values the wrong way. A fraction such as
 * `0.25` belongs on `formatNumber` with `notation: 'percent'`.
 *
 * Non-finite input is handed to `Intl` unchanged, which renders `NaN` and `∞`.
 *
 * @param preferences - The resolved preferences supplying the locale.
 * @param value - The percent point to render.
 * @param options - Overrides the fractional-digit ceiling for this call.
 * @returns The formatted string. Trailing zeros are dropped.
 */
export const formatPercent = (
  preferences: PreferenceValues,
  value: number,
  options?: PercentFormatOptions
): string =>
  numberFormat(preferences.locale, {
    style: 'unit',
    unit: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: options?.maximumDigits ?? DEFAULT_MAXIMUM_DIGITS,
  }).format(value);
